// src/pages/InvestorDashboard.js
import React, { useState, useEffect, useMemo } from "react";
import axios from 'axios';
import '../utils/axiosConfig'; // runs the 401/403 logout logic globally
import { Link } from "react-router-dom";

import {
  FaBook,
  FaUser,
  FaComments,
  FaCalendar,
  FaQuestionCircle,
  FaTrashAlt,
  FaLock,
  FaUserCog,
} from "react-icons/fa";
// import ChatBot from './Chatbot';
import "../Style/Dashboard.css";
import "../Style/Responsive.css";

// Dummy fallback investees
const dummyInvestees = [
  {
    id: 1,
    investee_name: "No matches yet",
    sector: "—",
    status: "pending",
    token_size: 0,
    funded_ammount: 0,
    pitch_deck: [],
  },
];

// Pie‐chart palette
const PIE_COLORS = [
  "#ACEAFF",
  "#7213EA",
  "#00338D",
  "#FD349C",
  "#1E49E2",
  "#00B8F5",
  "#00C0AE",
  "#FF9A00",
  "#FF3D00",
];

export default function InvestorDashboard() {
  const [currentUser, setCurrentUser] = useState({
    business_name: "",
    avatar: "",
  });
  const [matches, setMatches] = useState([]);
  const [usingDummy, setUsingDummy] = useState(false);
  const [avatar, setAvatar] = useState(null);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [isSidebarCollapsed] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);

  // grab once
  const token = localStorage.getItem("token");
  const headers = useMemo(() => ({ Authorization: `Token ${token}` }), [token]);

  // — fetch profile
  useEffect(() => {
    if (!token) return void (window.location.href = "/login");

    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/profile/`, { headers })
      .then(({ data }) => {
        setCurrentUser({
          business_name: data.business_name || data.name || "Investor",
          avatar: data.avatar,
        });
      })
      .catch(console.error);
  }, [token, headers]);

  // — fetch matches
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return void (window.location.href = "/login");

    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/matches/`, {
        headers: { Authorization: `Token ${token}` },
      })
      .then(({ data }) => {
        if (!Array.isArray(data) || data.length === 0) {
          setUsingDummy(true);
          setMatches(dummyInvestees);
          return;
        }

        const mapped = data.map((m) => {
          const inv = m.investee || {};
          return {
            id: m.id,
            status: m.status,
            // 👇 pull from the right field
            investee_name: inv.investee_name || "—",
            sector: inv.sector_type || "—",
            // 👇 default to 0 if missing
            token_size: Number(inv.capital_needed || 0),
            funded_ammount: Number(m.funded_ammount || 0),
            pitch_link: inv.pitch_link || "",
          };
        });

        setMatches(mapped);
        setUsingDummy(false);
      })
      .catch((err) => {
        console.error("Failed to load matches:", err.response || err);
        setUsingDummy(true);
        setMatches(dummyInvestees);
      });
  }, []);

  const updateLocal = (matchId, fields) =>
    setMatches((ms) =>
      ms.map((m) => (m.id === matchId ? { ...m, ...fields } : m))
    );

  const handleAvatarChange = (e) => {
    const f = e.target.files[0];
    if (f) setAvatar(URL.createObjectURL(f));
  };

  // Approve handler
  const handleApprove = (m) => {
    const url = `${process.env.REACT_APP_API_BASE_URL}/matches/${m.id}/status/`;
    axios
      .patch(
        url,
        { status: "approved" },
        { headers: { Authorization: `Token ${token}` } }
      )
      .then(() => {
        updateLocal(m.id, { status: "approved" });
      })
      .catch(() => {
        alert("Failed to approve.");
      });
  };

  // Approve handler
  const handleDecline = (m) => {
    const url = `${process.env.REACT_APP_API_BASE_URL}/matches/${m.id}/status/`;
    axios
      .patch(
        url,
        { status: "rejected" },
        { headers: { Authorization: `Token ${token}` } }
      )
      .then(() => {
        updateLocal(m.id, { status: "rejected" });
      })
      .catch(() => {
        alert("Failed to decline – please try again.");
      });
  };


  // — compute metrics
  const totalMatched = matches.length;
  const totalFunded = matches.reduce(
    (sum, m) => sum + (m.funded_ammount || 0),
    0
  );
  const sectorCounts = matches.reduce((acc, m) => {
    acc[m.sector] = (acc[m.sector] || 0) + 1;
    return acc;
  }, {});
  const sectors = Object.entries(sectorCounts).map(([name, count]) => ({
    name,
    count,
  }));
  const totalSectors = sectors.reduce((sum, s) => sum + s.count, 0);

  // — pie slices
  let angle = 0;
  const slices = sectors
    .map((s, i) => {
      const portion = (s.count / totalSectors) * 360;
      const start = angle,
        end = angle + portion;
      angle += portion;
      return `${PIE_COLORS[i % PIE_COLORS.length]} ${start}deg ${end}deg`;
    })
    .join(",");
  return (
    <div className="dashboard-container">
      {/* Side Navigation */}
      <aside className={`sidebar ${isSidebarCollapsed ? "collapsed" : ""}`}>
        <button
          className={`mobile-nav-toggle ${isMobileNavOpen ? "active" : ""}`}
          onClick={() => setIsMobileNavOpen(!isMobileNavOpen)}
        >
          {isMobileNavOpen ? "✕" : "☰"}
        </button>

        <div id="avatar-upload">
          <label htmlFor="avatarInput">
            <img
              src={
                avatar ||
                currentUser.avatar ||
                "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcToK4qEfbnd-RN82wdL2awn_PMviy_pelocqQ&s"
              }
              alt="Avatar"
              id="avatar-img"
            />
          </label>
          <p className="greeting">
            Hi, Investor :
            <span id="greeting-name">{currentUser.business_name}</span>
          </p>
          <input
            id="avatarInput"
            type="file"
            accept="image/*"
            onChange={handleAvatarChange}
            style={{ display: "none" }}
          />
        </div>

        <nav className={`nav-links ${isMobileNavOpen ? "active" : ""}`}>
          <ul>
            <li>
              <Link to="/profile">
                <FaUser className="nav-icon" />
                Profile
              </Link>
            </li>
            <li>
              <Link to="/programs-loggedIn">
                <FaBook className="nav-icon" />
                Programs
              </Link>
            </li>
            {/* <li><Link to="/investor-funding">< FaHandHoldingUsd /> investor-funding</Link></li> */}

            <li>
              <Link
                to="https://teams.microsoft.com/l/channel/19%3A1ff8550e81e046f2b352083b757a2520%40thread.tacv2/Trade%20Connect?groupId=8a4e4ff1-6630-402a-ad41-998ede82f756&tenantId=deff24bb-2089-4400-8c8e-f71e680378b2"
                className="tooltip"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaComments className="nav-icon" />
                Trade Connect
                <span className="tooltip-text">
                  Join our investor & investee community for live discussions!
                </span>
              </Link>
            </li>
            <li>
              <Link to="/training-calendar">
                <FaCalendar /> Training Calendar
              </Link>
            </li>
            <li>
              <Link to="/events-calendar">
                <FaCalendar /> Events Calendar
              </Link>
            </li>
            <li>
              <Link to="/support">
                <FaQuestionCircle className="nav-icon" />
                Support
              </Link>
            </li>

            <li className="manage-account …">
              <li
                className={`manage-account ${manageOpen ? "open" : ""}`}
                onClick={() => setManageOpen((o) => !o)}
              >
                <span className="manage-trigger">
                  <FaUserCog className="nav-icon" /> Manage Account
                </span>

                {/* modal overlay */}
                {manageOpen && (
                  <div
                    className="manage-modal-overlay"
                    onClick={() => setManageOpen(false)}
                  >
                    <div
                      className="manage-modal"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        className="close-btn"
                        onClick={() => setManageOpen(false)}
                      >
                        ×
                      </button>
                      <h3>Manage Account</h3>
                      <ul>
                        <li>
                          <Link to="/change-password">
                            <FaLock className="nav-icon" /> Change Password
                          </Link>
                        </li>
                        <li>
                          <Link to="/delete-account">
                            <FaTrashAlt className="nav-icon" /> Delete Account
                          </Link>
                        </li>
                      </ul>
                    </div>
                  </div>
                )}
              </li>
            </li>
          </ul>
        </nav>
      </aside>

      <main className="main-content">
        <div className="greeting-container">
          <h2>Welcome,</h2>
          <h2 id="greeting-name">{currentUser.business_name}!</h2>
        </div>

        {usingDummy && (
          <p className="dev-notice">
            <div className="dev-notice">
              <em>ℹ️ Investor Dashboard</em>
              <br />
              Here you can review your matched opportunities, view submitted
              pitch decks, approve or adjust funding, and track the status of
              each investee in real time. Use the actions column to manage
              approvals, funding, or provide feedback on declined matches.
            </div>
          </p>
        )}

        {/* Metrics */}
        <div className="metrics-grid investor-metrics">
          <div className="metric-item">
            <span className="metric-label">Total Matched</span>
            <span className="metric-value">{totalMatched}</span>
          </div>
          <div className="metric-item">
            <span className="metric-label">Sectors</span>
            <div
              className="pie-chart"
              style={{ background: `conic-gradient(${slices})` }}
            >
              <div className="pie-center"></div>
            </div>
            <div
              className="sector-tooltip"
              data-sectors={sectors
                .map((s) => `${s.name}: ${s.count}`)
                .join("\n")}
            >
              Hover for sectors
            </div>
          </div>
          <div className="metric-item">
            <span className="metric-label">Total Funded</span>
            <span className="metric-value">
              ${" "}
              {totalFunded.toLocaleString(undefined, {
                minimumFractionDigits: 2,
              })}
            </span>
          </div>
        </div>

        {/* Matched Opportunities */}
        <div className="investee-table">
          <h3>Matched Opportunities</h3>
          <table>
            <thead>
              <tr>
                <th>Status</th>
                <th>Investee</th>
                <th>Sector</th>
                <th>Token Size</th>
                <th>Funded</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {matches.map((m) => (
                <tr key={m.id}>
                  <td>
                    <span className={`status-dot ${m.status}`} />
                    <span className={`status-label ${m.status}`}>
                      {m.status.charAt(0).toUpperCase() + m.status.slice(1)}
                    </span>
                  </td>
                  <td>{m.investee_name}</td>
                  <td>{m.sector}</td>
                  <td>${m.token_size.toLocaleString()}</td>
                  <td>
                    {m.funded_ammount > 0
                      ? `$${m.funded_ammount.toLocaleString()}`
                      : "-"}
                  </td>

                  <td className="actions-cell">
                    <button
                      className="btn-approve"
                      onClick={() => handleApprove(m)}
                      disabled={m.status === "approved"}
                    >
                      Approve
                    </button>
                    <button
                      className="btn-decline"
                      onClick={() => handleDecline(m)}
                      disabled={m.status === "rejected"}
                    >
                      Decline
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>


        {/* <ChatBot /> */}
      </main>
    </div>
  );
}
